import React, { memo, useCallback } from "react";
import { Link } from "react-router-dom";
import { HiOutlineEye } from "react-icons/hi";
import moment from "moment";
import { IMAGE_URL } from "store/services/URL";
import { BsFillFileEarmarkPdfFill } from "react-icons/bs";
import { SiMicrosoftexcel } from "react-icons/si";
import { MdMarkEmailRead } from "react-icons/md";
import CheckBox from "components/common/checkBox/checkbox";
import { baseURLImage, formatAmount, getNameProfile } from "Utils/utils";

const getColorOfStatus = (status) =>
  status === "Extract Generated"
    ? "border bg-green-100 border-green-300"
    : status === "Export to Xero"
      ? "border bg-green-200 border-green-500"
      : status === "Mark As Paid"
        ? "border pl-4 pr-4 bg-green-400 border-green-700"
        : "border pl-10 pr-10 bg-orange-200 border-orange-300";

const RosterInvoicesTable = ({ item, handleCheckboxChange, isChecked }) => {
  const handleDownload = useCallback((pdfUrl) => {
    window.open(`${IMAGE_URL}${pdfUrl}`, "_blank");
  }, []);

  const handleImageError = useCallback((e, fname, lname) => {
    e.target.src = getNameProfile(fname, lname);
  }, []);

  const onCheckboxToggle = useCallback(
    (checked) => {
      handleCheckboxChange(checked, item?.uuid);
    },
    [handleCheckboxChange, item?.uuid]
  );

  const statusLabel =
    item?.status === "Export to Xero" ? "Exported to Xero" : item?.status;

  return (
    <tr className="text-center even:bg-white odd:bg-gray-50">
      <td className="whitespace-nowrap pl-3 py-4 text-sm text-gray-500">
        <div className="text-gray-500">
          <CheckBox
            handleChange={onCheckboxToggle}
            checked={isChecked}
            isDisabled={item?.status === "Void"}
          />
        </div>
      </td>
      <td className=" px-3 py-4 text-sm text-gray-500 text-start ">
        <Link
          className="text-xs font-normal text-blue"
          to={`/rostering/roster-invoice-preview/${item?.uuid}`}
        >
          {item?.invoice_number}
        </Link>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
        <div className="capitalize truncate max-w-[150px]">
          <div className="flex items-center">
            <div className="h-10 w-10 flex-shrink-0">
              <img
                onError={(e) =>
                  handleImageError(
                    e,
                    item?.participant?.first_name,
                    item?.participant?.last_name
                  )
                }
                className="capitalize object-cover h-10 w-10 rounded-full"
                src={
                  item?.participant?.photo
                    ? baseURLImage(item?.participant?.photo)
                    : getNameProfile(
                        item?.participant?.first_name,
                        item?.participant?.last_name || " "
                      )
                }
                alt={`${item?.participant?.first_name || ""} ${item?.participant?.last_name || ""}`}
              />
            </div>
            <div
              className="ml-1 text-start capitalize flex-1 cursor-default"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
              title={`${item?.participant?.first_name} ${item?.participant?.last_name}`.trim()}
            >
              {item?.participant?.first_name} {item?.participant?.last_name}
            </div>
          </div>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-center capitalize">
        <div
          className="ml-1 capitalize flex-1 cursor-default"
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
          title={`${item?.created_by?.name}`.trim()}
        >
          {item?.created_by?.name}
        </div>
      </td>

      <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
        <div className="font-normal">
          {moment(item?.created_at).format("ddd, D MMM YY")}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
        <div className="font-normal">
          {item?.date_from ? moment(item?.date_from).format("ddd, D MMM YY") : "N/A"}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
        <div className="font-normal">
          {item?.date_to ? moment(item?.date_to).format("ddd, D MMM YY") : "N/A"}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
        <div className="capitalize font-normal">
          <span
            className={`text-black text-xs p-1 rounded-lg font-normal ${getColorOfStatus(item?.status)}`}
          >
            {statusLabel}
          </span>
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
        <div className="font-normal">{item?.total_timesheets ? item?.total_timesheets : 0}</div>
      </td>

      <td className="whitespace-nowrap px-3 py-4 text-sm text-center">
        <div className="font-normal">
          ${item?.total_amount ? formatAmount(parseFloat(item?.total_amount).toFixed(2)) : 0}
          {item?.has_other_lines && (
            <span
              className="ml-1 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700"
              title="Has other line items"
            >
              +Other
            </span>
          )}
        </div>
      </td>

      <td>
        <div className="flex justify-center gap-x-2 p-1">
          <Link
            className="icon-eye-wrapper inline-block"
            to={`/rostering/roster-invoice-preview/${item?.uuid}`}
            title="View"
          >
            <HiOutlineEye className="icon-size" />
          </Link>
          <span
            title={item?.file_pdf ? "Download pdf" : "Not Extracted As Pdf"}
            onClick={item?.file_pdf ? () => handleDownload(item.file_pdf) : null}
            className={`text-rose-600  border border-rose-600  p-1 rounded-md ${
              item?.file_pdf
                ? "cursor-pointer hover:text-white hover:border-rose-600 hover:bg-rose-600 hover:transition-all-custom"
                : "cursor-not-allowed opacity-30"
            }`}
          >
            <BsFillFileEarmarkPdfFill className="h-4 w-4" />
          </span>

          <span
            title={item?.file_csv ? "Download csv" : "Not Extracted As CSV"}
            onClick={item?.file_csv ? () => handleDownload(item?.file_csv) : null}
            className={`text-green-700 border border-green-700  p-1 rounded-md ${
              item?.file_csv
                ? "cursor-pointer hover:text-white border hover:border-green-700 hover:bg-green-700 hover:transition-all-custom"
                : "cursor-not-allowed opacity-30"
            }`}
          >
            <SiMicrosoftexcel className="h-4 w-4" />
          </span>

          <span
            title={item?.email_sent ? "Invoice Email Sent" : "Invoice Email Not Sent"}
            className={`text-purple-700   border border-purple-700  p-1 rounded-md ${
              item?.email_sent
                ? "cursor-pointer hover:text-white hover:border-purple-700 hover:bg-purple-700 hover:transition-all-custom"
                : "cursor-not-allowed opacity-30"
            }`}
          >
            <MdMarkEmailRead className="h-4 w-4" />
          </span>
        </div>
      </td>
    </tr>
  );
};

export default memo(RosterInvoicesTable);
